import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { ApiResponse, ReactApiDataActions, ReportType, User } from 'constants/api';
import { compose } from 'recompose';
import { Roles } from 'constants/literals';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import ViewContainer from 'components/utils/ViewContainer';
import { Row, Col } from 'reactstrap';
import SyncButton from 'components/utils/SyncButton';
import { get } from 'lodash';
import ReportAddModal from 'components/modals/ReportAddModal';
import { isAdmin } from 'utils/userUtils';
import withUserRoles from 'hocs/withUserRoles';
import withUser from 'hocs/withUser';
import ReportInfo from 'components/cards/ReportInfo';
import CustomPagination from 'components/utils/CustomPagination';
import { connect } from 'react-redux';
import { updateAppPathAction } from 'redux/app/appActions';
import { ReduxState } from 'createStore';

export const reportsDefaultParams = {
    per_page: 2,
    page: 1,
    sort: 'name',
    order: 'asc',
};

interface ConnectProps {
    reportsBinding: ApiDataBinding<ApiResponse<{ reports: ReportType[] }>>;
    userRoles: Roles[];
    currentUser: User;
    apiDataActions: ReactApiDataActions;
    initPagination: { initPageNumber: number; initPerPage: number; initSort: string; initOrder: string };
    updatePagination: (initPagination: {
        initPageNumber?: number;
        initPerPage?: number;
        initSort?: string;
        initOrder?: string;
    }) => void;
}

type Props = ConnectProps;

const enhance = compose<ConnectProps, {}>(
    connect(
        (state: ReduxState) => ({
            initPagination: get(state, 'app.reports.initPagination', reportsDefaultParams),
        }),
        (dispatch) => ({
            updatePagination: (initPagination: {
                initPageNumber: number;
                initPerPage: number;
                initSort: string;
                initOrder: string;
            }) => {
                dispatch(updateAppPathAction('reports.initPagination', initPagination));
            },
        })
    ),
    withUser,
    withApiData(
        {
            reportsBinding: 'getReports',
        },
        () => ({
            reportsBinding: reportsDefaultParams,
        })
    ),
    withUserRoles
);

const ReportsView: FunctionComponent<Props> = ({
    reportsBinding,
    userRoles,
    initPagination,
    updatePagination,
}) => {
    const [pageNumber, setPageNumber] = useState(initPagination.initPageNumber);
    const [perPage, setPerPage] = useState(initPagination.initPerPage);
    const [sortBy, setSortBy] = useState(initPagination.initSort);
    const [sortOrder, setSortOrder] = useState(initPagination.initOrder);
    const { t } = useTranslation();
    const refetchReports = (perPage: number, pageNumber: number, sortBy: string, sortOrder: string) => {
        const baseParams = {
            page: pageNumber,
            per_page: perPage,
            sort: sortBy,
            order: sortOrder,
        };
        return reportsBinding.perform(baseParams);
    };
    const fetchReports = useCallback(refetchReports, [perPage, pageNumber, sortBy, sortOrder]);
    const totalReports = get(reportsBinding, 'data.meta.total_items', 1);
    const reportsData = get(reportsBinding, 'data.data.reports', []);
    const refreshReports = () => {
        fetchReports(perPage, pageNumber, sortBy, sortOrder);
    };
    const perPageOptions = [
        { value: 2, name: '2' },
        { value: 4, name: '4' },
        { value: 8, name: '8' },
        { value: 12, name: '12' },
        { value: totalReports, name: 'All' },
    ];
    const sortByOptions = [
        { value: 'name', name: 'Name' },
        { value: 'frequency', name: 'Frequency' },
        { value: 'roles', name: 'Roles' },
        { value: 'updated_at', name: 'Updated' },
    ];

    useEffect(() => {
        refreshReports();
    }, [perPage, pageNumber, sortBy, sortOrder]);

    return (
        <>
            <Helmet title={t('reportsCommon.pageTitle')} />
            <ViewContainer>
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <h2>{t('reportsCommon.pageTitle')}</h2>
                                <SyncButton
                                    className="ml-2"
                                    title={t('reportsCommon.refreshButton')}
                                    onClick={() => {
                                        refreshReports();
                                    }}
                                />
                            </div>
                            {isAdmin(userRoles) && (
                                <ReportAddModal
                                    pageNumber={pageNumber}
                                    perPage={perPage}
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                />
                            )}
                        </div>
                        <hr />
                    </Col>
                </Row>
                {reportsData.length > 0 ? (
                    <>
                        <Row>
                            {reportsData.map((report: ReportType, index: number) => {
                                return (
                                    <Col md={6} key={index}>
                                        <ReportInfo
                                            report={report}
                                            isUserAdmin={isAdmin(userRoles)}
                                            pageNumber={pageNumber}
                                            perPage={perPage}
                                            sortBy={sortBy}
                                            sortOrder={sortOrder}
                                        />
                                    </Col>
                                );
                            })}
                        </Row>
                        <CustomPagination
                            totalItems={totalReports}
                            perPage={perPage}
                            pageNumber={pageNumber}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            sorting={true}
                            perPageOptions={perPageOptions}
                            sortByOptions={sortByOptions}
                            onChange={(itemsPerPage, currentPageNumber, sortItemsBy, sortItemsOrder) => {
                                setPageNumber(currentPageNumber);
                                setPerPage(itemsPerPage);
                                setSortBy(sortItemsBy);
                                setSortOrder(sortItemsOrder);
                                updatePagination({
                                    initPageNumber: currentPageNumber,
                                    initPerPage: itemsPerPage,
                                    initSort: sortItemsBy,
                                    initOrder: sortItemsOrder,
                                });
                            }}
                        />
                    </>
                ) : (
                    <> No Data </>
                )}
            </ViewContainer>
        </>
    );
};

export default enhance(ReportsView);
